import type { CriterionScoringDraft } from '@matter_extractor/shared';
import { CRITERION_SCORING_TOP } from '@matter_extractor/shared';

/**
 * « Aperçu de l'échelle » (refonte modulaire §6, maquette `Main.dc.html`), as
 * arithmetic: where each band starts and stops on a rail 0 to 100 wide.
 *
 * It is a module of its own for the reason `protocol-lanes.ts` gives about the
 * frise: jsdom has no layout, so a percentage computed inside a component is a
 * number no test can read. Here the rail is the ONE drawing of this screen
 * whose whole meaning is proportion - a preview with equal segments would be a
 * picture of a different scale - so the numbers are the thing to hold.
 *
 * ## A scale being typed has holes, and the hole is the point
 *
 * The bands are contiguous by construction (§6: a lower bound is the previous
 * upper), which means ONE band with no upper bound leaves its neighbour with no
 * lower bound either - « la tranche 3 est sans fin, la tranche 4 sans début ».
 * A hole therefore spans from the last bound that is known to the next one that
 * is, however many bands it swallows, and the editor hatches it. That is also
 * why a non-increasing bound opens a hole rather than drawing a segment
 * backwards: a rail cannot show a band that ends before it starts, and drawing
 * it at zero width would hide the very defect the preview is for.
 */

/** One band that can be drawn: a start, a width, and whether the criterion is filled there. */
export interface ScaleSegment {
  readonly left: number;
  readonly width: number;
  /** At or above the plancher - painted in bleu nuit (§6). */
  readonly filled: boolean;
  /** At or above the passable band and below the plancher - painted in jaune (#110). */
  readonly passable: boolean;
}

/** A stretch of the rail whose bounds are not known yet - hatched, never filled. */
export interface ScaleHole {
  readonly left: number;
  readonly width: number;
}

/** A number written under the rail: a known boundary, the plancher's one marked. */
export interface ScaleBoundary {
  readonly value: number;
  readonly isPlancher: boolean;
  /** Where « passable » begins (#110) - marked like the plancher, in jaune. */
  readonly isPassable: boolean;
}

export interface ScalePreview {
  readonly segments: ScaleSegment[];
  readonly holes: ScaleHole[];
  readonly boundaries: ScaleBoundary[];
}

export function scalePreview(scoring: CriterionScoringDraft): ScalePreview {
  const segments: ScaleSegment[] = [];
  const holes: ScaleHole[] = [];

  /** The last boundary that is known - where the next band starts. */
  let cursor = 0;
  /** Where the hole being walked through opened, or null outside one. */
  let hole: number | null = null;
  /** The lower bound of the plancher band, once it is known to be drawable. */
  let plancherAt: number | null = null;
  /** The same for the passable band, when the scale marks one (#110). */
  let passableAt: number | null = null;

  /*
    A `for` and not a `forEach`, and it is not a preference: `hole` is written
    by the walk and read after it, and TypeScript stops narrowing a variable a
    closure assigns - the version with a callback typed the last `hole !== null`
    as unreachable.
  */
  for (let index = 0; index < scoring.bands.length; index += 1) {
    const upper = scoring.bands[index]?.upper ?? null;
    if (index === scoring.plancher && hole === null) plancherAt = cursor;
    if (index === scoring.passable && hole === null) passableAt = cursor;

    // No bound, or one that does not clear the last known boundary: the band
    // cannot be placed, so it joins (or opens) the hole and the cursor stays.
    if (upper === null || upper <= cursor) {
      if (hole === null) hole = cursor;
      continue;
    }

    if (hole !== null) {
      // This bound is the first known one after the hole: it CLOSES it, which
      // is what makes a hole span every band it swallowed rather than one.
      holes.push({ left: hole, width: upper - hole });
      hole = null;
    } else {
      const filled = index >= scoring.plancher;
      segments.push({
        left: cursor,
        width: upper - cursor,
        filled,
        passable: !filled && scoring.passable !== null && index >= scoring.passable,
      });
    }
    cursor = upper;
  }

  // A scale whose last band has no bound never reaches 100: the hole runs to
  // the end of the rail, because that is where the scale ends whatever anybody
  // typed (§6).
  if (hole !== null) holes.push({ left: hole, width: CRITERION_SCORING_TOP - hole });

  const stops = [
    0,
    ...segments.map((segment) => segment.left + segment.width),
    ...holes.map((one) => one.left + one.width),
  ];
  const boundaries = [...new Set(stops)]
    .sort((one, other) => one - other)
    .map((value) => ({
      value,
      isPlancher: plancherAt !== null && value === plancherAt,
      isPassable: passableAt !== null && value === passableAt,
    }));

  return { segments, holes, boundaries };
}
